import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { CirclePicker } from 'react-color';
import {
  Button,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap';
import { injectIntl } from 'react-intl';

import {
  APIcreateFileStatus,
  APIupdateFileStatus,
} from '../../../api/Files';

import { fetchFileStatuses } from '../../../actions/Files';

import { addFlashMessage } from '../../../helpers/FlashMessages';

import ProcessingSpinner from '../../ProcessingSpinner';

import ErrorBoundary from '../../ErrorBoundary';

const statusColors = [
  '#f44336',
  '#fe5a58',
  '#e91e63',
  '#9c27b0',
  '#673ab7',
  '#1073ba',
  '#2196f3',
  '#3e95fe',
  '#00bcd4',
  '#009688',
  '#4caf50',
  '#28c29a',
  '#cddc39',
  '#fec958',
  '#ffc107',
  '#ff9800',
  '#ff5d00',
  '#795548',
  '#607d8b',
  '#000000',
];

class FileStatusEditModal extends Component {
  constructor(props) {
    super(props);

    this.state = {
      name: null,
      color: '#f44336',
      isDefault: false,
      action: '',
      errors: [],
      saving: false,
    };

    this.inputRef = React.createRef();

    this.handleChange = this.handleChange.bind(this);
    this.handleChangeColor = this.handleChangeColor.bind(this);
    this.handleUpdate = this.handleUpdate.bind(this);
  }

  componentDidMount() {
    this.setInitialState();
  }

  setInitialState() {
    const { status } = this.props;

    if (status) {
      if (status.name) {
        this.setState({ name: status.name }, () => {
          this.inputRef.selectionStart = this.inputRef.value.length;
          this.inputRef.selectionEnd = this.inputRef.value.length;
        });
      }
      if (status.color) {
        this.setState({ color: status.color });
      }
      if (status.default) {
        this.setState({ isDefault: status.default });
      }
      if (status.action !== undefined) {
        this.setState({ action: status.action ?? '' });
      }
    }
  }

  handleUpdate() {
    const {
      intl,
      dispatch,
      status,
      afterCreateStatus,
      handleClose,
    } = this.props;
    const {
      name,
      color,
      isDefault,
      action,
    } = this.state;

    const errors = [];
    if (!name) {
      errors.push('name');
    }
    if (!color) {
      errors.push('color');
    }
    this.setState({ errors });
    if (errors.length > 0) {
      return;
    }

    this.setState({
      saving: true,
      errors: [],
    });

    if (status) {
      status.name = name;
      status.color = color;
      status.default = isDefault;
      status.action = action || null;
      APIupdateFileStatus(status).then(() => {
        dispatch(fetchFileStatuses());
        afterCreateStatus(status);
        addFlashMessage({
          type: 'success',
          text: intl.formatMessage({ defaultMessage: 'Your changes have been saved' }),
        });
        handleClose();
      });
    } else {
      APIcreateFileStatus({
        name,
        color,
        default: isDefault,
        action: action || null,
      }).then((response) => {
        dispatch(fetchFileStatuses());
        afterCreateStatus(response);
        addFlashMessage({
          type: 'success',
          text: intl.formatMessage({ defaultMessage: 'New file status has been created' }),
        });
        handleClose();
      });
    }
  }

  handleChange(e) {
    const {
      name,
      value,
      type,
      checked,
    } = e.target;
    const { state } = this;

    if (type === 'checkbox') {
      state[name] = checked;
    } else {
      state[name] = value;
    }
    this.setState(state);
  }

  handleChangeColor(color) {
    this.setState({ color: color.hex.toUpperCase() });
  }

  render() {
    const {
      intl,
      status,
      handleClose,
    } = this.props;
    const {
      name,
      color,
      isDefault,
      action,
      errors,
      saving,
    } = this.state;

    return (
      <ErrorBoundary>
        <ModalHeader toggle={handleClose} className="modal-header-center">
          {status ? intl.formatMessage({ defaultMessage: 'Edit file status' }) : intl.formatMessage({ defaultMessage: 'New file status' })}
        </ModalHeader>
        <ModalBody>
          <Form className="edit-status-form" onSubmit={(e) => { e.preventDefault(); this.handleUpdate(); }}>
            <FormGroup>
              <Label for="statusName">
                {intl.formatMessage({ defaultMessage: 'Status name' })}
              </Label>
              <Input
                type="text"
                id="statusName"
                name="name"
                autoFocus={true}
                innerRef={(ref) => { this.inputRef = ref; }}
                disabled={saving}
                defaultValue={name}
                onChange={this.handleChange}
                invalid={errors.includes('name')}
              />
              {errors.includes('name') && (
                <FormFeedback>{intl.formatMessage({ defaultMessage: 'This field is required' })}</FormFeedback>
              )}
            </FormGroup>
            <FormGroup>
              <Label for="statusColor">
                {intl.formatMessage({ defaultMessage: 'Status color' })}
              </Label>
              <CirclePicker
                color={color}
                colors={statusColors}
                width="100%"
                onChangeComplete={this.handleChangeColor}
              />
            </FormGroup>
            <FormGroup>
              <Label for="isDefault" className="switch">
                <div className="switch-control input-inner">
                  <Input
                    type="checkbox"
                    id="isDefault"
                    name="isDefault"
                    checked={isDefault}
                    value={true}
                    onChange={this.handleChange}
                  />
                  <i />
                </div>
                <div className="switch-details">
                  <span>{intl.formatMessage({ defaultMessage: 'Set this status as the default for all uploaded files' })}</span>
                </div>
              </Label>
            </FormGroup>
            <FormGroup>
              <Label for="statusAction">
                {intl.formatMessage({ defaultMessage: 'Action' })}
              </Label>
              <Input
                type="select"
                className="custom-select"
                name="bulk-actions"
                bsSize="sm"
                value={action}
                onChange={(e) => { this.setState({ action: e.target.value }); }}
              >
                <option value="">
                  {intl.formatMessage({ defaultMessage: 'No Action' })}
                </option>
                <option value="disable_download">
                  {intl.formatMessage({ defaultMessage: 'Disable downloading' })}
                </option>
                <option value="watermark">
                  {intl.formatMessage({ defaultMessage: 'Add watermark' })}
                </option>
              </Input>
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <div className="buttons-group">
            <Button
              color="secondary"
              disabled={saving}
              onClick={handleClose}
            >
              {intl.formatMessage({ defaultMessage: 'Cancel' })}
            </Button>
            <Button
              color="primary"
              disabled={saving}
              onClick={this.handleUpdate}
            >
              <ProcessingSpinner show={saving} />
              {intl.formatMessage({ defaultMessage: 'Save' })}
            </Button>
          </div>
        </ModalFooter>
      </ErrorBoundary>
    );
  }
}

FileStatusEditModal.propTypes = {
  intl: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  handleClose: PropTypes.func.isRequired,
  status: PropTypes.object,
  afterCreateStatus: PropTypes.func,
};

FileStatusEditModal.defaultProps = {
  status: null,
  afterCreateStatus: () => {},
};

export default connect()(injectIntl(FileStatusEditModal));
